/**
 * Üye adreslerinden mahalle ve cadde/sokak ayıklar.
 *
 * Adresler tek biçimli geliyor:
 *   "MAHMUDİYE MAHALLESİ 26. MOBİLYA SK. NO: 9 İNEGÖL/BURSA"
 * Harici bir servise ya da koordinata ihtiyaç yok; rota ekranı bu iki
 * alana göre grupluyor.
 */

const TR_UPPER = (s) =>
  String(s ?? "")
    .replace(/i/g, "İ")
    .replace(/ı/g, "I")
    .toUpperCase();

// "İNEGÖL / BURSA", "İNEGÖL/BURSA", sonda kalan noktalama
const KUYRUK = /\s*[,/-]?\s*İNEGÖL\s*[/-]\s*BURSA\s*\.?$/;

// JavaScript'in \b sınırı yalnızca [A-Za-z0-9_] üzerinden çalışır; "MAHALLESİ"
// sonundaki İ kelime karakteri sayılmadığı için \b hiç eşleşmiyor. Bu yüzden
// sınırı elle yazıyoruz: ekten sonra harf/rakam gelmemeli.
const SINIR = "(?![A-ZÇĞİIÖŞÜ0-9])";
const MAHALLE_EKI = new RegExp(`\\s*(MAHALLESİ|MAHALLESI|MAHALLE|MAH|MH)\\.?${SINIR}`);
const YOL_EKI = new RegExp(
  `\\s*(CADDESİ|CADDESI|CADDE|CAD|BULVARI|BULVAR|BULV|BLV|BUL|SOKAĞI|SOKAGI|SOKAK|SOK|SK)\\.?${SINIR}`
);

// Mahalle eki taşımayan ama bilinen bölgeler. Sıra önemli: uzun olan önce.
const BİLİNEN_BÖLGELER = [
  ["ORGANİZE SANAYİ BÖLGESİ", "ORGANİZE SANAYİ"],
  ["ORGANİZE SANAYİ", "ORGANİZE SANAYİ"],
  ["BÜYÜK SANAYİ", "BÜYÜK SANAYİ"],
  ["KÜÇÜK SANAYİ", "KÜÇÜK SANAYİ"],
  ["YENİ SANAYİ", "YENİ SANAYİ"],
  ["ÇELTİKÇİ KÖYÜ", "ÇELTİKÇİ"],
  ["DEYDİNLER KÖYÜ", "DEYDİNLER"],
  ["ERTUĞRULGAZİ", "ERTUĞRULGAZİ"],
];

/**
 * Veride aynı yerin farklı yazımları var; tek isme indiriyoruz.
 *
 * Sağ taraftaki karşılıklar 2026-09-03'te Google Haritalar'da tek tek
 * doğrulandı — kaydın kendi adres metnine değil, Haritalar'ın o sokak için
 * yazdığı resmî mahalleye bakıldı.
 *
 * DİKKAT: "YENİCE" ile "YENİCEKÖY" İnegöl'de AYRI iki mahalle; birleştirilmez.
 * Aynı şekilde "YENİ" de gerçek bir mahalle (Millet Cad. oraya düşüyor).
 */
const EŞLEME = {
  "SÜLEYMANİYEOSB": "SÜLEYMANİYE OSB",
  "SÜLEYMANİYE OSB": "SÜLEYMANİYE OSB",
  "HAMZABEY OSB": "HAMZABEY OSB",
  "OSB": "OSB",
  // "YENİ SANAYİ MAHMUDİYE MAHALLESİ 9.MOBİLYA SOKAK" — zaten Mahmudiye.
  "YENİ SANAYİ MAHMUDİYE": "MAHMUDİYE",
  // "YENİ SAN. YENİCE MAHALLESİ OSMANBEY CADDE" — Osman Bey Cd. → Yenice.
  "YENİ SAN. YENİCE": "YENİCE",
  // "YENİCE KÖY MAH. MOBİLYA SK." — araya boşluk kaçmış Yeniceköy.
  "YENİCE KÖY": "YENİCEKÖY",
  // OSB kayıtları: "1. Cadde" Haritalar'da Süleymaniye'ye düşüyor.
  "ORGANİZE SANAYİ BÖLGESİ SÜLEYMANİYE": "SÜLEYMANİYE OSB",
  "ORGANİZE SANAYİ": "SÜLEYMANİYE OSB",
};

/**
 * "Yeni Sanayi" bir mahalle değil; Mahmudiye ile Akhisar'a yayılan bir sanayi
 * bölgesi. Mahalle adı okunamayan birkaç adres de var. İkisini de sokak adından
 * çözüyoruz — Google Haritalar'da doğrulanmış eşleşmeler.
 */
const SOKAK_MAHALLE = {
  "TABAKHANE CAD.": "MAHMUDİYE",
  "FATİH CAD.": "AKHİSAR",
  "AHMET AKYOLLU CAD.": "SİNANBEY",
  // "FATİH SULTAN MEHMET BULVARI YENİCE YOLU ÜZERİ NO:34". Haritalar'da
  // "Fatih Sultan Mehmet Han Blv No:34" Yeniceköy'e düşüyor; adresteki
  // "Yenice yolu" ve mobilya sitesi bağlamı da oraya işaret ediyor.
  // (Aynı adın "Han"sız hâli şehir içinde başka bir caddeye çıkıyor.)
  "FATİH SULTAN MEHMET CAD.": "YENİCEKÖY",
};

// Sokak adından mahalle çıkarmanın denenebileceği durumlar.
const ŞÜPHELİ_MAHALLE = new Set([null, "YENİ SANAYİ"]);

function düzelt(ad) {
  const t = ad.replace(/\s+/g, " ").trim().replace(/^[.,-]+|[.,-]+$/g, "");
  return EŞLEME[t] || t;
}

/** Adresten { mahalle, sokak } döndürür. Çıkarılamayan alan null olur. */
export function parseAddress(address) {
  let s = TR_UPPER(address).replace(/\s+/g, " ").trim();
  if (!s) return { mahalle: null, sokak: null };
  s = s.replace(KUYRUK, "").trim();

  let mahalle = null;
  let kalan = s;

  const m = s.match(MAHALLE_EKI);
  if (m && m.index > 0) {
    mahalle = düzelt(s.slice(0, m.index));
    kalan = s.slice(m.index + m[0].length);
  } else {
    for (const [desen, ad] of BİLİNEN_BÖLGELER) {
      if (s.startsWith(desen)) {
        // düzelt(): bu yol EŞLEME tablosunu atlıyordu, "ORGANİZE SANAYİ" gibi
        // kayıtlar tek başına ayrı bir mahalle olarak listeleniyordu.
        mahalle = düzelt(ad);
        kalan = s.slice(desen.length);
        break;
      }
    }
  }

  let sokak = null;
  const y = kalan.match(YOL_EKI);
  if (y && y.index > 0) {
    // "NO:" ile başlayan parçaya kadar olan kısım yol adıdır
    const ham = kalan.slice(0, y.index);
    const ad = düzelt(ham);
    if (ad) sokak = `${ad} ${y[1].startsWith("CAD") || y[1].startsWith("BUL") ? "CAD." : "SK."}`;
  }

  // Mahalle okunamadıysa ya da mahalle sanılan şey aslında bir sanayi bölgesiyse,
  // sokak adından çöz.
  if (sokak && ŞÜPHELİ_MAHALLE.has(mahalle) && SOKAK_MAHALLE[sokak]) {
    mahalle = SOKAK_MAHALLE[sokak];
  }

  return { mahalle, sokak };
}

/**
 * Üyeleri mahalle → sokak ağacına dizer.
 * Dönüş: [{ ad, üyeler, sokaklar: [{ ad, üyeler }] }] — üye sayısına göre sıralı.
 */
export function groupByArea(members) {
  const mahalleler = new Map();

  for (const m of members) {
    const { mahalle, sokak } = parseAddress(m.address);
    const mAd = mahalle || "Belirsiz bölge";
    const sAd = sokak || "Sokak belirtilmemiş";

    if (!mahalleler.has(mAd)) mahalleler.set(mAd, { ad: mAd, üyeler: [], sokakMap: new Map() });
    const kayıt = mahalleler.get(mAd);
    kayıt.üyeler.push(m);

    if (!kayıt.sokakMap.has(sAd)) kayıt.sokakMap.set(sAd, { ad: sAd, üyeler: [] });
    kayıt.sokakMap.get(sAd).üyeler.push(m);
  }

  return Array.from(mahalleler.values())
    .map((k) => ({
      ad: k.ad,
      üyeler: k.üyeler,
      sokaklar: Array.from(k.sokakMap.values()).sort(
        (a, b) => b.üyeler.length - a.üyeler.length || a.ad.localeCompare(b.ad, "tr")
      ),
    }))
    .sort((a, b) => b.üyeler.length - a.üyeler.length || a.ad.localeCompare(b.ad, "tr"));
}

/** Telefonun harita uygulamasında tek adresi açar. */
export function mapSearchUrl(address) {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address || "")}`;
}

/**
 * Birden çok durak için yol tarifi bağlantısı.
 * Google Maps URL'i ara durak sayısını sınırlıyor; ilk 10 adresi alıyoruz.
 */
export function mapRouteUrl(addresses) {
  const temiz = (addresses || []).filter(Boolean).slice(0, 10);
  if (temiz.length === 0) return null;
  if (temiz.length === 1) return mapSearchUrl(temiz[0]);
  const hedef = encodeURIComponent(temiz[temiz.length - 1]);
  const ara = temiz.slice(0, -1).map(encodeURIComponent).join("|");
  return `https://www.google.com/maps/dir/?api=1&destination=${hedef}&waypoints=${ara}&travelmode=driving`;
}
